// modules/storage/index.ts
var SIGNED_URL_TTL = 3600;
var BUCKETS = {
  EMPLOYEES: "employees",
  INCIDENTS: "incidents",
  ENCOUNTERS: "encounters",
  UNITS: "units",
  ITEM_CATALOG: "item-catalog",
  CHAT: "chat",
  EXTERNAL_ACCESS: "external-access",
  DOCUMENTS: "documents",
  BRANDING: "branding",
  SIGNATURES: "signatures"
};
var PUBLIC_BUCKETS = /* @__PURE__ */ new Set([BUCKETS.BRANDING]);
var KNOWN_BUCKETS = /* @__PURE__ */ new Set([
  ...Object.values(BUCKETS),
  "headshots",
  "patient-photos",
  "credentials",
  "chat-files"
]);
async function resolveUrl(supabase, stored, expiresIn = SIGNED_URL_TTL) {
  if (!stored) return void 0;
  if (stored.startsWith("data:")) return stored;
  if (stored.startsWith("http://") || stored.startsWith("https://")) return stored;
  const slash = stored.indexOf("/");
  if (slash < 0) return void 0;
  const bucket = stored.slice(0, slash);
  if (!KNOWN_BUCKETS.has(bucket)) return void 0;
  const path = stored.slice(slash + 1);
  if (PUBLIC_BUCKETS.has(bucket)) {
    return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
  }
  const { data } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);
  return data?.signedUrl ?? void 0;
}
async function resolveUrls(supabase, stored, expiresIn = SIGNED_URL_TTL) {
  const result = /* @__PURE__ */ new Map();
  const bucketGroups = /* @__PURE__ */ new Map();
  for (const s of stored) {
    if (!s || result.has(s)) continue;
    if (s.startsWith("data:") || s.startsWith("http://") || s.startsWith("https://")) {
      result.set(s, s);
      continue;
    }
    const slash = s.indexOf("/");
    if (slash < 0) continue;
    const bucket = s.slice(0, slash);
    if (!KNOWN_BUCKETS.has(bucket)) continue;
    const path = s.slice(slash + 1);
    if (PUBLIC_BUCKETS.has(bucket)) {
      result.set(s, supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl);
      continue;
    }
    let group = bucketGroups.get(bucket);
    if (!group) {
      group = [];
      bucketGroups.set(bucket, group);
    }
    group.push({ stored: s, path });
  }
  await Promise.all(
    Array.from(bucketGroups.entries()).map(async ([bucket, items]) => {
      const { data } = await supabase.storage.from(bucket).createSignedUrls(items.map((i) => i.path), expiresIn);
      if (data) {
        for (let i = 0; i < data.length; i++) {
          const signed = data[i].signedUrl;
          if (signed) result.set(items[i].stored, signed);
        }
      }
    })
  );
  return result;
}
async function upload(supabase, bucket, path, data, contentType, options) {
  const { error } = await supabase.storage.from(bucket).upload(path, data, { contentType, upsert: options?.upsert ?? false });
  if (error) throw error;
  return `${bucket}/${path}`;
}
async function uploadAmaPdf(supabase, encounterUuid, consentId, data) {
  if (encounterUuid) return upload(supabase, BUCKETS.ENCOUNTERS, `${encounterUuid}/ama-forms/${consentId}`, data, "application/pdf", { upsert: true });
  return upload(supabase, BUCKETS.DOCUMENTS, `ama/${consentId}`, data, "application/pdf", { upsert: true });
}
async function uploadCatalogImage(supabase, catalogItemId, data, contentType) {
  return upload(supabase, BUCKETS.ITEM_CATALOG, `${catalogItemId}/photo`, data, contentType, { upsert: true });
}
async function uploadChatAttachment(supabase, channelId, data, contentType) {
  return upload(supabase, BUCKETS.CHAT, `${channelId}/uploads/${crypto.randomUUID()}`, data, contentType);
}
async function uploadCompClaimPdf(supabase, encounterUuid, claimId, data) {
  if (encounterUuid) return upload(supabase, BUCKETS.ENCOUNTERS, `${encounterUuid}/comp-claims/${claimId}`, data, "application/pdf", { upsert: true });
  return upload(supabase, BUCKETS.DOCUMENTS, `comp-claims/${claimId}`, data, "application/pdf", { upsert: true });
}
async function uploadConsentSignature(supabase, incidentId, consentId, data, contentType = "image/png") {
  const folder = incidentId ? `consent/${incidentId}` : "unlinked";
  return upload(supabase, BUCKETS.SIGNATURES, `${folder}/${consentId}`, data, contentType);
}
async function uploadAMASignature(supabase, incidentId, consentId, role, data, contentType = "image/png") {
  const folder = incidentId ? `ama/${incidentId}` : "unlinked";
  return upload(supabase, BUCKETS.SIGNATURES, `${folder}/${consentId}/${role}`, data, contentType);
}
async function uploadConsentToTreatPdf(supabase, encounterUuid, consentId, data) {
  if (encounterUuid) return upload(supabase, BUCKETS.ENCOUNTERS, `${encounterUuid}/consent-forms/${consentId}`, data, "application/pdf", { upsert: true });
  return upload(supabase, BUCKETS.DOCUMENTS, `consent-to-treat/${consentId}`, data, "application/pdf", { upsert: true });
}
async function uploadCredential(supabase, employeeId, canonicalName, data, contentType) {
  return upload(supabase, BUCKETS.EMPLOYEES, `${employeeId}/credentials/${canonicalName}`, data, contentType, { upsert: true });
}
async function uploadEmployeeHeadshot(supabase, employeeId, data, contentType) {
  return upload(supabase, BUCKETS.EMPLOYEES, `${employeeId}/photo`, data, contentType, { upsert: true });
}
async function uploadEncounterPhoto(supabase, encounterUuid, data, contentType) {
  return upload(supabase, BUCKETS.ENCOUNTERS, `${encounterUuid}/photos/${crypto.randomUUID()}`, data, contentType);
}
async function uploadExpenseReceipt(supabase, incidentId, data, contentType) {
  return upload(supabase, BUCKETS.INCIDENTS, `${incidentId}/expenses/${crypto.randomUUID()}`, data, contentType);
}
async function uploadExternalAvatar(supabase, accessCodeId, data, contentType) {
  return upload(supabase, BUCKETS.EXTERNAL_ACCESS, `${accessCodeId}/avatar`, data, contentType, { upsert: true });
}
async function uploadICS214Pdf(supabase, incidentId, ics214Id, data) {
  return upload(supabase, BUCKETS.INCIDENTS, `${incidentId}/ics214/${ics214Id}`, data, "application/pdf", { upsert: true });
}
async function uploadICS214Signature(supabase, incidentId, ics214Id, data, contentType = "image/png") {
  return upload(supabase, BUCKETS.SIGNATURES, `ics214/${incidentId}/${ics214Id}`, data, contentType, { upsert: true });
}
async function uploadIncidentContract(supabase, incidentId, data, contentType) {
  return upload(supabase, BUCKETS.INCIDENTS, `${incidentId}/contract`, data, contentType, { upsert: true });
}
async function uploadNemsisXml(supabase, encounterUuid, data) {
  return upload(supabase, BUCKETS.ENCOUNTERS, `${encounterUuid}/nemsis-exports/${crypto.randomUUID()}`, data, "application/xml");
}
async function uploadOrgBackground(supabase, data, contentType) {
  return upload(supabase, BUCKETS.BRANDING, "background", data, contentType, { upsert: true });
}
async function uploadOrgDocument(supabase, data, contentType) {
  return upload(supabase, BUCKETS.DOCUMENTS, crypto.randomUUID(), data, contentType);
}
async function uploadOrgLogo(supabase, data, contentType) {
  return upload(supabase, BUCKETS.BRANDING, "logo", data, contentType, { upsert: true });
}
async function uploadUnitPhoto(supabase, unitId, data, contentType) {
  return upload(supabase, BUCKETS.UNITS, `${unitId}/photo`, data, contentType, { upsert: true });
}
async function uploadVehicleDocument(supabase, unitId, data, contentType) {
  return upload(supabase, BUCKETS.UNITS, `${unitId}/documents/${crypto.randomUUID()}`, data, contentType);
}
export {
  resolveUrl,
  resolveUrls,
  uploadAMASignature,
  uploadAmaPdf,
  uploadCatalogImage,
  uploadChatAttachment,
  uploadCompClaimPdf,
  uploadConsentSignature,
  uploadConsentToTreatPdf,
  uploadCredential,
  uploadEmployeeHeadshot,
  uploadEncounterPhoto,
  uploadExpenseReceipt,
  uploadExternalAvatar,
  uploadICS214Pdf,
  uploadICS214Signature,
  uploadIncidentContract,
  uploadNemsisXml,
  uploadOrgBackground,
  uploadOrgDocument,
  uploadOrgLogo,
  uploadUnitPhoto,
  uploadVehicleDocument
};
